import { showToast } from '../../../engine/toast';
import { adminCreateUser, KYC_STATUSES, USER_ROLES } from '../admin-api';
import type { PaneCtx } from '../panes/types';
import { checkboxField, errorText, readForm, selectField, submitRow, textField } from './fields';

/**
 * Create a player account.
 *
 * `password` is required by core's CreateUserDto (`@MinLength(6)`), so this is
 * the one place an operator types a password for someone else. Editing an
 * existing account's password is a separate, deliberate action — see
 * `edit-player.ts`.
 */
export function openCreateUser(ctx: PaneCtx): void {
  const handle = ctx.openModal({
    title: 'Create user',
    subtitle: 'The account is usable immediately; the player is not emailed.',
    body: `
      <form id="create-user" data-form="create-user" class="flex flex-col gap-3">
        ${textField('email', 'Email', '', { type: 'email', required: true })}
        ${textField('username', 'Call sign', '', { placeholder: 'Derived from the email if left blank' })}
        ${textField('password', 'Password', '', { type: 'password', required: true, hint: 'At least 6 characters.' })}
        ${selectField('role', 'Role', USER_ROLES, 'PLAYER')}
        ${selectField('kycStatus', 'KYC status', KYC_STATUSES, 'NONE')}
        ${checkboxField('emailVerified', 'Email already verified', true)}
      </form>
      ${submitRow('Create account', 'create-user')}`,

    onSubmit(kind, form, h) {
      if (kind !== 'create-user') return;
      const v = readForm(form);
      const password = String(v.password ?? '');
      if (password.length < 6) {
        h.setError('Password must be at least 6 characters.');
        return;
      }
      h.setBusy(true);
      void (async () => {
        try {
          await adminCreateUser({
            email: String(v.email ?? ''),
            username: v.username ? String(v.username) : undefined,
            password,
            role: String(v.role ?? 'PLAYER'),
            kycStatus: String(v.kycStatus ?? 'NONE'),
            emailVerified: Boolean(v.emailVerified),
          });
          showToast('Account created.', 'success');
          h.close();
          await ctx.reload('players');
        } catch (err) {
          // Both channels on purpose: showToast is a no-op unless the toast
          // container was mounted, so a dialog relying on it alone can fail
          // silently.
          h.setError(errorText(err, 'Could not create that account.'));
          h.setBusy(false);
        }
      })();
    },
  });

  void handle;
}
